/**
 * Number formatting for the UI.
 *
 * All currency formatting lives here and all currency arithmetic lives in
 * economy.ts, so the `number` → `Decimal` swap described in the README's
 * "Big numbers" note touches these two modules and nothing else.
 */

const SCALES = [
  '',
  'thousand',
  'million',
  'billion',
  'trillion',
  'quadrillion',
  'quintillion',
  'sextillion',
  'septillion',
  'octillion',
  'nonillion',
  'decillion',
] as const;

/**
 * Digit grouping, "1,234,567": exactly toLocaleString('en-US'). Whole numbers,
 * which is everything the game groups, are done by hand. Anything else goes
 * to one shared Intl formatter, made on first need: toLocaleString builds a
 * new one on every call, which made it the most expensive thing in a frame,
 * and even the one shared formatter cost a slow phone 130 ms to create at
 * start-up, before the first egg was drawn.
 */
let grouped: Intl.NumberFormat | null = null;
export function groupDigits(value: number): string {
  if (Number.isSafeInteger(value) && !Object.is(value, -0)) {
    const digits = String(Math.abs(value));
    let out = digits.slice(0, ((digits.length - 1) % 3) + 1);
    for (let i = out.length; i < digits.length; i += 3) out += `,${digits.slice(i, i + 3)}`;
    return value < 0 ? `-${out}` : out;
  }
  grouped ??= new Intl.NumberFormat('en-US');
  return grouped.format(value);
}

/** Below this, values read better with digit grouping than a scale name. */
const NAMED_SCALE_FLOOR = 1e6;

function trimZeros(s: string): string {
  return s.includes('.') ? s.replace(/\.?0+$/, '') : s;
}

/** Three significant digits: 1.23, 12.3, 123. */
function sigDigits(value: number): string {
  const decimals = value < 10 ? 2 : value < 100 ? 1 : 0;
  return trimZeros(value.toFixed(decimals));
}

/** Formats with a scale name: "1.23 million", "4.5 billion". */
function formatLarge(value: number): string {
  let tier = Math.floor(Math.log10(value) / 3);
  let scaled = value / 1000 ** tier;

  // Rounding to three significant digits can itself reach the next scale
  // (999,999,999.9 rounds to "1000 million"), so the boundary test has to run
  // on the rounded value rather than the raw one.
  if (Number(sigDigits(scaled)) >= 1000) {
    tier += 1;
    scaled = value / 1000 ** tier;
  }

  const name = SCALES[tier];
  if (name === undefined) {
    // Past decillion, fall back to exponential rather than inventing names.
    return value.toExponential(2).replace('e+', 'e');
  }
  return `${sigDigits(scaled)} ${name}`;
}

/** Eggs banked. Whole eggs below a million, scale names above. */
/** Places after the point once a scale name is carrying the magnitude. */
const PRECISE_DECIMALS = 3;

/**
 * The egg count for the readout that is watched while it climbs.
 *
 * The same scale names as `formatEggs`, but three places after the point
 * rather than three significant digits. Three significant digits is right for
 * a figure that is read once — "4.5 billion" in a sentence — and wrong for a
 * number someone is staring at, because past ten million the last digit it
 * shows stops moving and the counter looks stuck. The places are kept even
 * when they are zeros, so the readout does not change width as it climbs.
 *
 * Below the first scale name nothing is abbreviated, so it is the same
 * grouped integer as everywhere else.
 */
export function formatEggsPrecise(value: number): string {
  const v = Math.max(0, value);
  if (v < NAMED_SCALE_FLOOR) return groupDigits(Math.floor(v));

  let tier = Math.floor(Math.log10(v) / 3);
  let scaled = v / 1000 ** tier;
  // Rounding can itself reach the next scale, so the boundary test runs on
  // the rounded value rather than the raw one.
  if (Number(scaled.toFixed(PRECISE_DECIMALS)) >= 1000) {
    tier += 1;
    scaled = v / 1000 ** tier;
  }

  const name = SCALES[tier];
  if (name === undefined) {
    // Past decillion, fall back to exponential rather than inventing names.
    return v.toExponential(2).replace('e+', 'e');
  }
  return `${scaled.toFixed(PRECISE_DECIMALS)} ${name}`;
}

export function formatEggs(value: number): string {
  const v = Math.max(0, value);
  if (v < NAMED_SCALE_FLOOR) return groupDigits(Math.floor(v));
  return formatLarge(v);
}

/** A rate (eggs per second). Keeps decimals while small, so 0.1 EPS is visible. */
export function formatRate(value: number): string {
  const v = Math.max(0, value);
  if (v === 0) return '0';
  if (v < 1000) {
    // 999.5 and up rounds to 1000, which the next line would write "1,000".
    const s = sigDigits(v);
    return s === '1000' ? '1,000' : s;
  }
  if (v < NAMED_SCALE_FLOOR) return groupDigits(Math.floor(v));
  return formatLarge(v);
}

/**
 * Suffixes for the compact form, the usual idle-game set: quadrillion is "Qa"
 * so it cannot be mistaken for quintillion's "Qi". Runs to vigintillion
 * (1e63); past that it falls back to exponential.
 */
const SHORT_SCALES = [
  '',
  'K',
  'M',
  'B',
  'T',
  'Qa',
  'Qi',
  'Sx',
  'Sp',
  'Oc',
  'No',
  'Dc',
  'Ud',
  'Dd',
  'Td',
  'Qad',
  'Qid',
  'Sxd',
  'Spd',
  'Ocd',
  'Nod',
  'Vg',
] as const;

/** Compact form for floating numbers and shop prices: "+1", "+12", "+1.5K", "+3.2M". */
export function formatShort(value: number, separator = ''): string {
  const v = Math.max(0, value);
  if (v < 1000) return String(Math.floor(v));
  let tier = Math.floor(Math.log10(v) / 3);
  let scaled = v / 1000 ** tier;
  if (Number(sigDigits(scaled)) >= 1000) {
    tier += 1;
    scaled = v / 1000 ** tier;
  }
  const suffix = SHORT_SCALES[tier];
  if (suffix === undefined) return v.toExponential(1).replace('e+', 'E');
  return `${sigDigits(scaled)}${separator}${suffix}`;
}

/**
 * How long the herd would take to lay `eggs` at `eps`, for a jackpot's
 * caption: "15 minutes", "an hour", "3 hours". Empty when it is under a
 * minute or there is no rate, where a time would say nothing useful.
 */
export function formatEggSpan(eggs: number, eps: number): string {
  if (!(eps > 0) || !(eggs > 0)) return '';
  const minutes = Math.round(eggs / eps / 60);
  if (minutes < 1) return '';
  if (minutes < 60) return minutes === 1 ? 'a minute' : `${minutes} minutes`;
  const hours = Math.round(minutes / 60);
  return hours === 1 ? 'an hour' : `${hours} hours`;
}

/** A span of seconds as "5m 12s", "2h 5m", or "8h". Never shows more than two units. */
export function formatDuration(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return m > 0 ? `${h}h ${m}m` : `${h}h`;
  if (m > 0) return sec > 0 ? `${m}m ${sec}s` : `${m}m`;
  return `${sec}s`;
}

/**
 * How many eggs a click throws off, for what the click was worth.
 *
 * The bounds are the span of a single era rather than of the whole game: a
 * click is worth 1 when an era starts and about 200,000 by the time it can
 * end, and fossils push that higher every era after. Pinning the top to an
 * all-time figure would leave the burst stuck at its maximum forever, so it
 * saturates at 100,000 — reached right about when the extinction unlocks, so
 * the full range is used once per era.
 *
 * Log-spaced between the two, one more egg per ×3.7. Zero pays nothing and
 * throws nothing, which is the honest answer under the Hands Off expedition.
 */
export const CLICK_EGGS_FLOOR = 10;
export const CLICK_EGGS_CEILING = 100_000;
export const CLICK_EGGS_MAX = 8;

export function clickEggCount(value: number): number {
  // Also catches NaN, which would otherwise floor to something absurd.
  if (!(value > 0)) return 0;
  if (value <= CLICK_EGGS_FLOOR) return 1;
  const decades = Math.log10(CLICK_EGGS_CEILING) - Math.log10(CLICK_EGGS_FLOOR);
  const perEgg = decades / (CLICK_EGGS_MAX - 1);
  const steps = (Math.log10(value) - Math.log10(CLICK_EGGS_FLOOR)) / perEgg;
  return Math.min(CLICK_EGGS_MAX, 1 + Math.floor(steps));
}
